/**
 * Chips de completude — pill mobile-first; Completos/Incompletos usam tons semânticos do DS quando ativos.
 */
import { cn } from '@/lib/utils';
import { cnFilterChip, type FilterChipTone } from '../../../components/shared/filters/filterChipStyles';
import type { ContactChipCounts, ContactCompletenessChip } from '../hooks/useContactFilters';

interface ContactFilterChipsProps {
    active: ContactCompletenessChip;
    onChange: (chip: ContactCompletenessChip) => void;
    counts: ContactChipCounts;
}

const CHIPS: { id: ContactCompletenessChip; label: string; tone?: FilterChipTone }[] = [
    { id: 'all', label: 'Todos' },
    { id: 'complete', label: 'Completos', tone: 'success' },
    { id: 'incomplete', label: 'Incompletos', tone: 'danger' },
];

function countBadgeClass(isActive: boolean, tone: FilterChipTone | undefined): string {
    if (isActive && tone === 'success') {
        return 'border border-success/30 bg-surface-0 text-success-text';
    }
    if (isActive && tone === 'danger') {
        return 'border border-error/30 bg-surface-0 text-danger-text';
    }
    return 'bg-surface-subtle text-default';
}

export function ContactFilterChips({ active, onChange, counts }: ContactFilterChipsProps) {
    return (
        <nav className="flex shrink-0 gap-s2" aria-label="Filtros de contatos">
            {CHIPS.map((chip) => {
                const isActive = active === chip.id;
                const count = counts[chip.id];
                const showCount = chip.id !== 'all' && typeof count === 'number';

                return (
                    <button
                        key={chip.id}
                        type="button"
                        onClick={() => onChange(isActive ? 'all' : chip.id)}
                        className={cnFilterChip(isActive, 'pill', chip.tone ?? 'primary')}
                        aria-pressed={isActive}
                    >
                        {chip.label}
                        {showCount && (
                            <span
                                className={cn(
                                    'inline-flex min-h-5 min-w-5 items-center justify-center rounded-full px-s2 font-mono text-2xs font-medium leading-none tabular-nums',
                                    countBadgeClass(isActive, chip.tone),
                                )}
                            >
                                {count}
                            </span>
                        )}
                    </button>
                );
            })}
        </nav>
    );
}
